import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageSquare, Send, Loader2, Settings2, Smartphone, PlayCircle } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const fmtDateTime = (s) => { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? s : d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }); };

export default function SmsHerinneringen() {
  const { t } = useT();
  const { planning } = useData();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTel, setTestTel] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState('');

  const load = useCallback(() => { carsysApi.getMessagingInstellingen().then(setCfg).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const upcoming = useMemo(() => {
    const now = new Date();
    return (planning || [])
      .filter(p => (p.klantEmail || p.klantId) && ['Bevestigd', 'Gepland'].includes(p.status) && p.start && new Date(p.start) >= now)
      .sort((a, b) => new Date(a.start) - new Date(b.start))
      .slice(0, 25);
  }, [planning]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, kanaal: 'email' };
      delete payload.authTokenSet;
      const s = await carsysApi.setMessagingInstellingen(payload);
      setCfg(s);
      toast.success(t('Opgeslagen', 'Opgeslagen'));
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const test = async () => {
    const isEmail = cfg?.kanaal === 'email';
    if (!testTel) { toast.error(isEmail ? t('Geef een e-mailadres op', 'Geef een e-mailadres op') : t('Geef een telefoonnummer op', 'Geef een telefoonnummer op')); return; }
    setTestBusy(true);
    try { const r = await carsysApi.testMessaging(testTel); toast.success(t('Testbericht verwerkt', 'Testbericht verwerkt'), { description: r.status }); }
    catch (e) { toast.error(t('Test mislukt', 'Test mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setTestBusy(false); }
  };

  const runNow = async () => {
    setRunBusy(true);
    try { const r = await carsysApi.runMessaging(); toast.success(t('Verwerkt', 'Verwerkt'), { description: `${r.sent || 0} ${t('herinneringen verstuurd', 'herinneringen verstuurd')}` }); load(); }
    catch (e) { toast.error(t('Verwerken mislukt', 'Verwerken mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setRunBusy(false); }
  };

  const sendOne = async (p) => {
    setSendBusy(p.id);
    try { const r = await carsysApi.sendPlanningReminder(p.id); toast.success(t('Herinnering verstuurd', 'Herinnering verstuurd'), { description: r.status }); }
    catch (e) { toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSendBusy(''); }
  };

  if (!cfg) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}><Loader2 className="spin" /></div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const isEmail = true;

  return (
    <div className="cs-page" data-testid="sms-herinneringen-page" style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <MessageSquare size={22} color="#2563eb" />
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a' }}>{t('Afspraakherinneringen', 'Afspraakherinneringen')}</h1>
      </div>
      <p style={{ color: '#64748b', margin: '0 0 18px', fontSize: 14 }}>
        {isEmail
          ? t('Stuur automatisch een afspraakherinnering via e-mail. Geen Twilio nodig — er wordt gemaild naar het e-mailadres van de klant.', 'Stuur automatisch een afspraakherinnering via e-mail. Geen Twilio nodig — er wordt gemaild naar het e-mailadres van de klant.')
          : t('Stuur automatisch een afspraakherinnering via SMS of WhatsApp (Twilio). Vul je eigen Twilio-gegevens in.', 'Stuur automatisch een afspraakherinnering via SMS of WhatsApp (Twilio). Vul je eigen Twilio-gegevens in.')}
      </p>

      <div style={{ display: 'grid', gap: 16 }}>
        <div style={card} data-testid="msg-settings">
          <div style={sectTitle}><Settings2 size={16} /> {isEmail ? t('E-mailherinneringen', 'E-mailherinneringen') : t('Twilio-instellingen', 'Twilio-instellingen')}</div>
          <label style={toggle}>
            <input type="checkbox" data-testid="msg-enabled" checked={!!cfg.enabled} onChange={e => set('enabled', e.target.checked)} />
            <span>{t('Automatische afspraakherinneringen inschakelen', 'Automatische afspraakherinneringen inschakelen')}</span>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 14 }}>
            <Field label={t('Uren vóór afspraak', 'Uren vóór afspraak')}>
              <input type="number" min="1" max="168" style={inp} data-testid="msg-uren" value={cfg.urenVooraf} onChange={e => set('urenVooraf', parseInt(e.target.value || '24', 10))} />
            </Field>
            {!isEmail && <>
            <Field label="Account SID">
              <input style={inp} data-testid="msg-sid" value={cfg.accountSid || ''} onChange={e => set('accountSid', e.target.value)} placeholder="ACxxxxxxxx" />
            </Field>
            <Field label={`Auth Token ${cfg.authTokenSet ? '(' + t('ingesteld', 'ingesteld') + ')' : ''}`}>
              <input type="password" style={inp} data-testid="msg-token" value={cfg.authToken || ''} onChange={e => set('authToken', e.target.value)} placeholder={cfg.authTokenSet ? '********' : ''} />
            </Field>
            <Field label={t('SMS-afzender (nummer)', 'SMS-afzender (nummer)')}>
              <input style={inp} data-testid="msg-sms-from" value={cfg.smsFrom || ''} onChange={e => set('smsFrom', e.target.value)} placeholder="+31612345678" />
            </Field>
            <Field label={t('WhatsApp-afzender', 'WhatsApp-afzender')}>
              <input style={inp} data-testid="msg-wa-from" value={cfg.whatsappFrom || ''} onChange={e => set('whatsappFrom', e.target.value)} placeholder="whatsapp:+14155238886" />
            </Field>
            </>}
          </div>
          <div style={{ marginTop: 12 }}>
            <Field label={t('Berichttekst (gebruik {naam} {garage} {datum} {tijd} {kenteken})', 'Berichttekst (gebruik {naam} {garage} {datum} {tijd} {kenteken})')}>
              <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} data-testid="msg-template" value={cfg.template || ''} onChange={e => set('template', e.target.value)} />
            </Field>
          </div>
          <button style={{ ...primaryBtn, marginTop: 12 }} data-testid="msg-save" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={15} className="spin" /> : null} {t('Opslaan', 'Opslaan')}
          </button>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'end', flexWrap: 'wrap', borderTop: '1px solid #eef2f7', paddingTop: 16 }}>
            <Field label={isEmail ? t('Test-e-mailadres', 'Test-e-mailadres') : t('Testnummer', 'Testnummer')}><input style={{ ...inp, minWidth: 200 }} data-testid="msg-test-tel" value={testTel} onChange={e => setTestTel(e.target.value)} placeholder={isEmail ? 'naam@voorbeeld.nl' : '+31612345678'} /></Field>
            <button style={ghostBtn} data-testid="msg-test-btn" onClick={test} disabled={testBusy}>{testBusy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} {t('Stuur testbericht', 'Stuur testbericht')}</button>
            <button style={ghostBtn} data-testid="msg-run-btn" onClick={runNow} disabled={runBusy}>{runBusy ? <Loader2 size={14} className="spin" /> : <PlayCircle size={14} />} {t('Nu verwerken', 'Nu verwerken')}</button>
          </div>
          {!isEmail && (!cfg.accountSid || !cfg.authTokenSet) && (
            <div style={{ marginTop: 14, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#92400e' }} data-testid="msg-mock-note">
              {t('Geen Twilio-gegevens ingevuld: berichten worden gelogd (MOCK) tot je je Account SID/Auth Token invult.', 'Geen Twilio-gegevens ingevuld: berichten worden gelogd (MOCK) tot je je Account SID/Auth Token invult.')}
            </div>
          )}
          {isEmail && (
            <div style={{ marginTop: 14, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#1e40af' }} data-testid="msg-email-note">
              {t('E-mailkanaal gekozen: herinneringen gaan via je ingestelde e-mail (SMTP/Resend) naar het e-mailadres van de klant. Geen Twilio nodig.', 'E-mailkanaal gekozen: herinneringen gaan via je ingestelde e-mail (SMTP/Resend) naar het e-mailadres van de klant. Geen Twilio nodig.')}
            </div>
          )}
        </div>

        <div style={card} data-testid="msg-upcoming">
          <div style={sectTitle}><Smartphone size={16} /> {isEmail ? t('Komende afspraken met e-mailadres', 'Komende afspraken met e-mailadres') : t('Komende afspraken met telefoonnummer', 'Komende afspraken met telefoonnummer')}</div>
          {upcoming.length === 0 ? <Empty>{isEmail ? t('Geen komende afspraken met e-mailadres', 'Geen komende afspraken met e-mailadres') : t('Geen komende afspraken met telefoonnummer', 'Geen komende afspraken met telefoonnummer')}</Empty> : (
            <table style={tbl}>
              <thead><tr><Th>{t('Wanneer', 'Wanneer')}</Th><Th>{t('Klant', 'Klant')}</Th><Th>{isEmail ? t('E-mail', 'E-mail') : t('Telefoon', 'Telefoon')}</Th><Th>{t('Status', 'Status')}</Th><Th right></Th></tr></thead>
              <tbody>
                {upcoming.map(p => (
                  <tr key={p.id} data-testid={`msg-appt-${p.id}`} style={{ borderTop: '1px solid #eef2f7' }}>
                    <Td>{fmtDateTime(p.start)}</Td>
                    <Td>{p.klantNaam || p.titel}</Td>
                    <Td>{isEmail ? (p.klantEmail || '—') : p.klantTelefoon}</Td>
                    <Td>{p.reminderSmsVerzonden ? <span style={{ color: '#16a34a' }}>{t('Verzonden', 'Verzonden')}</span> : p.status}</Td>
                    <Td right>
                      <button style={smallBtn} data-testid={`msg-send-${p.id}`} disabled={sendBusy === p.id} onClick={() => sendOne(p)}>
                        {sendBusy === p.id ? <Loader2 size={13} className="spin" /> : <Send size={13} />} {t('Stuur nu', 'Stuur nu')}
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => <div><label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>{label}</label>{children}</div>;
const Th = ({ children, right }) => <th style={{ textAlign: right ? 'right' : 'left', padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 700 }}>{children}</th>;
const Td = ({ children, right }) => <td style={{ textAlign: right ? 'right' : 'left', padding: '9px 10px', fontSize: 13.5, color: '#0f172a' }}>{children}</td>;
const Empty = ({ children }) => <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0', fontSize: 14 }}>{children}</div>;

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 2px 6px rgba(15,23,42,.04)' };
const tbl = { width: '100%', borderCollapse: 'collapse' };
const sectTitle = { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 14 };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box' };
const toggle = { display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 600, color: '#0f172a', cursor: 'pointer' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 18px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #2563eb', borderRadius: 7, background: '#eff6ff', color: '#2563eb', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' };
